import { ButtonBase } from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { Divider } from "@/components/Divider/Divider";
import { Image } from "@/components/Image/Image";
import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import type { FindUserPackageBookingsPackageBooking } from "@/types/codegen";

type Props = {
  booking: FindUserPackageBookingsPackageBooking;
  onClick: () => void;
};

export function TripBookingCard({ booking, onClick }: Props) {
  const intl = useIntl();

  const startDateFormatted = dayjs(booking.startDate).format("D MMMM, YYYY");
  const endDateFormatted = dayjs(booking.startDate)
    .add(booking.package.durationInDays, "day")
    .format("D MMMM, YYYY");

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        padding: "calc(var(--spacing))",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-2xl)",
        display: "block",
        width: "100%",
        textAlign: "initial",
      }}
    >
      <Image
        src={booking.package.imageUrls[0]}
        alt={booking.package.name}
        className="aspect-7/2 w-full rounded-t-xl object-cover"
      />

      <div className="p-3">
        <div className="mb-1 text-xl font-medium text-text-title">
          {booking.package.name}
        </div>
        <div className="text-sm text-text-tertiary">
          {booking.package.subtitle}
        </div>

        <div className="my-3">
          <Divider color="neutral-soft" />
        </div>

        <DetailsList className="mb-4">
          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel start date",
              description: "Label for the trip start / departure date",
            })}
            value={startDateFormatted}
          />

          <DetailsListItem
            title={intl.formatMessage({
              defaultMessage: "Travel end date",
              description: "Label for the trip end / return date",
            })}
            value={endDateFormatted}
          />
        </DetailsList>

        <Button as="span" variant="outlined" fullWidth size="small">
          {intl.formatMessage({
            defaultMessage: "Trip details",
            description: "Section heading for trip details summary",
          })}
        </Button>
      </div>
    </ButtonBase>
  );
}
